import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import LoadingButton from "@mui/lab/LoadingButton";
import api from "../../actprep-ui/utils/api";
import { apiUrls } from "../../actprep-ui/utils/urls";

function memberLabel(member) {
  if (!member) {
    return "";
  }
  if (member.email) {
    return `${member.name} (${member.email})`;
  }
  return member.name || "";
}

export default function CreateProgramChatDialog({
  open,
  chatType,
  suggestedName,
  lockedMembers,
  submitting,
  onClose,
  onCreate,
}) {
  const [name, setName] = useState("");
  const [addedMembers, setAddedMembers] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  const titlePrefix = chatType === "coaching" ? "Coaching" : "Parent";

  useEffect(() => {
    if (!open) {
      return;
    }
    setName(suggestedName || "");
    setAddedMembers([]);
    setSearchInput("");
    setSearchTerm("");
    setCandidates([]);
  }, [open, chatType, suggestedName]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [open, searchInput]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    let ignore = false;
    async function fetchCandidates() {
      setLoadingCandidates(true);
      try {
        const response = await api.get(apiUrls.messaging.users(), {
          params: {
            search_input: searchTerm,
            page: 0,
            size: 25,
          },
        });
        if (!ignore) {
          setCandidates(response?.data?.users || []);
        }
      } catch {
        if (!ignore) {
          setCandidates([]);
        }
      } finally {
        if (!ignore) {
          setLoadingCandidates(false);
        }
      }
    }
    fetchCandidates();
    return () => {
      ignore = true;
    };
  }, [open, searchTerm]);

  const lockedIds = useMemo(
    () => new Set(lockedMembers.map((member) => Number(member.id))),
    [lockedMembers]
  );

  const options = useMemo(() => {
    const seen = new Set();
    const nextOptions = [];
    for (const member of [...addedMembers, ...candidates]) {
      const id = Number(member.id);
      if (!id || lockedIds.has(id) || seen.has(id)) {
        continue;
      }
      seen.add(id);
      nextOptions.push(member);
    }
    return nextOptions;
  }, [addedMembers, candidates, lockedIds]);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Create {titlePrefix} Chat</DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Chat name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          fullWidth
          size="small"
          margin="normal"
          helperText="Leave blank to use the default name."
          slotProps={{ htmlInput: { maxLength: 255 } }}
          disabled={submitting}
        />
        <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
          Always included
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {lockedMembers.map((member) => (
            <Chip key={member.id} size="small" label={memberLabel(member)} />
          ))}
        </Box>
        <Autocomplete
          multiple
          disableCloseOnSelect
          filterSelectedOptions
          fullWidth
          options={options}
          value={addedMembers}
          loading={loadingCandidates}
          disabled={submitting}
          inputValue={searchInput}
          onInputChange={(_, value) => setSearchInput(value)}
          onChange={(_, value) => setAddedMembers(value)}
          filterOptions={(nextOptions) => nextOptions}
          isOptionEqualToValue={(option, value) =>
            Number(option.id) === Number(value.id)
          }
          getOptionLabel={(option) => memberLabel(option)}
          noOptionsText={
            loadingCandidates ? "Loading users..." : "No users found."
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Add people"
              placeholder="Search by name or email"
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>{memberLabel(option)}</li>
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={submitting}
          onClick={() =>
            onCreate({
              name: name.trim(),
              memberIds: [
                ...lockedMembers.map((member) => Number(member.id)),
                ...addedMembers.map((member) => Number(member.id)),
              ],
            })
          }
        >
          Create Chat
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
